import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiMapPin, FiRefreshCw, FiTruck } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import FAQ from '@/components/FAQ';
import Searchbar from '@/components/Searchbar';
import { TRACKING_NUMBER_LENGTH } from '@/constants';
import { useTranslation } from '@/i18n';

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const { handleSubmit, register, watch } = useForm<{
    trackingNumber: string;
  }>();

  useEffect(() => {
    const sub = watch(({ trackingNumber }) => {
      setIsButtonDisabled(trackingNumber?.length !== TRACKING_NUMBER_LENGTH);
    });
    return () => sub.unsubscribe();
  }, [watch]);

  const onSubmit = handleSubmit(({ trackingNumber }) => {
    navigate(`/track/${trackingNumber.trim().toUpperCase()}`);
  });

  const features = [
    { icon: FiTruck, label: t('home.features.live') },
    { icon: FiMapPin, label: t('home.features.map') },
    { icon: FiRefreshCw, label: t('home.features.reschedule') },
  ];

  return (
    <div className='relative isolate flex flex-1 flex-col overflow-x-hidden'>
      <div
        aria-hidden
        className='pointer-events-none absolute inset-0 -z-10 bg-[#eef3f4]'
      />
      <div
        aria-hidden
        className='pointer-events-none absolute inset-0 -z-10 opacity-[0.35]'
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(9,45,54,0.08) 1px, transparent 0)',
          backgroundSize: '28px 28px',
        }}
      />
      <div
        aria-hidden
        className='home-orb-drift pointer-events-none absolute -left-24 top-0 -z-10 h-80 w-80 rounded-full bg-[#449486]/15 blur-3xl'
      />
      <div
        aria-hidden
        className='home-orb-drift-delay pointer-events-none absolute -right-20 top-32 -z-10 h-96 w-96 rounded-full bg-[#092d36]/10 blur-3xl'
      />

      <section className='relative px-4 pb-10 pt-10 sm:px-6 sm:pb-14 sm:pt-14 md:pt-16'>
        <div className='mx-auto flex w-full max-w-2xl flex-col items-center text-center'>
          <div className='home-enter flex max-w-xl flex-col items-center gap-4 sm:gap-5'>
            <div
              aria-hidden
              className='mb-1 flex w-full max-w-[220px] items-center gap-3 sm:mb-2'
            >
              <span className='h-px flex-1 bg-gradient-to-r from-transparent to-greenSecondary/45' />
              <span className='h-1 w-1 rounded-full bg-greenSecondary/70' />
              <span className='h-px flex-1 bg-gradient-to-l from-transparent to-greenSecondary/45' />
            </div>

            <h1 className='font-headings-semibold text-[2rem] leading-[1.12] tracking-[-0.035em] sm:text-[2.75rem] sm:leading-[1.1]'>
              <span className='home-gradient-text bg-gradient-to-br from-backgroundGreen via-[#0a3540] to-greenSecondary bg-clip-text text-transparent'>
                {t('home.title')}
              </span>
            </h1>
            <p className='max-w-md font-content text-[1.0625rem] leading-[1.85] text-graySecondary sm:text-lg sm:leading-[1.9]'>
              {t('home.subtitle')}
            </p>
            <span
              aria-hidden
              className='mt-1 h-[3px] w-14 rounded-full bg-gradient-to-r from-greenSecondary/20 via-greenSecondary to-greenSecondary/20 sm:w-16'
            />
          </div>

          <form
            className='home-enter home-enter-delay-1 mt-10 w-full sm:mt-12'
            onSubmit={onSubmit}
          >
            <Searchbar
              cta={t('home.searchCta')}
              isButtonDisabled={isButtonDisabled}
              label={t('home.searchLabel')}
              placeholder={t('home.searchPlaceholder')}
              register={register}
              title={t('home.searchTitle')}
              variant='hero'
            />
          </form>

          <ul className='mt-8 grid w-full grid-cols-1 gap-3 sm:mt-10 sm:grid-cols-3 sm:gap-4'>
            {features.map(({ icon: Icon, label }, index) => (
              <li
                key={label}
                className={`home-enter home-feature-card flex items-center gap-3 rounded-2xl border border-white/70 bg-white/55 px-4 py-3.5 text-left shadow-[0_8px_24px_rgba(9,45,54,0.05)] backdrop-blur-sm sm:flex-col sm:items-center sm:gap-2.5 sm:px-3 sm:py-4 sm:text-center ${
                  index === 0
                    ? 'home-enter-delay-2'
                    : index === 1
                    ? 'home-enter-delay-3'
                    : 'home-enter-delay-4'
                }`}
              >
                <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-backgroundGreen text-white shadow-[0_4px_14px_rgba(9,45,54,0.2)] transition-transform duration-300 sm:h-10 sm:w-10'>
                  <Icon aria-hidden className='h-4 w-4' />
                </span>
                <span className='text-sm font-medium leading-snug text-backgroundGreen/85'>
                  {label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className='home-enter home-enter-delay-4 relative px-4 pb-12 sm:px-6 sm:pb-16'>
        <FAQ />
      </section>
    </div>
  );
};

export { Home };
